import * as L from "../mapbox";
import { createFeature, ColorWheel, FeatureLayerEvent, GeoJSONLayer, LayerAddedCallback } from "./common";

import { FeatureType, Feature } from "./api";

interface LandGeoJSONLayer extends GeoJSONLayer<L.MultiPolygon> {
    // Sets the given path options to each layer of the group that has a setStyle method.
    setStyle(style: L.PathOptions): L.FeatureGroup<L.MultiPolygon>;
}

interface Options {
    landMarkerThreshold: number;
}

export class LandLayer {
    constructor({landMarkerThreshold = 85 }) {
        this._landMarkerThreshold = landMarkerThreshold;
        this._featureLayer = null;
    }


    createLayerAdded(map: L.Map, nextColor: ColorWheel, featureAddCallback?: (f: Feature) => void): (e: FeatureLayerEvent<GeoJSONLayer<L.MultiPolygon>>) => void {
        return (e: FeatureLayerEvent<GeoJSONLayer<L.MultiPolygon>>) => {
            const layer = e.layer;
            const properties = layer.feature.properties;

            layer.setStyle({
                fillColor: properties.fillColor || nextColor(),
                fillOpacity: 0.5
            });

            if (properties.title) {
                layer.bindPopup("<div class='popup-title'>" + properties.title + "</div>");
            }

            if (typeof featureAddCallback === "function") {
                featureAddCallback(createFeature(layer, map, FeatureType.Land));
            }
        };
    }


    getFeatureLayer() : L.mapbox.FeatureLayerT<GeoJSONLayer<L.MultiPolygon>> {
        return this._featureLayer;
    }


    resetFeatureLayer(): void {
        // Creates new empty land feature layer
        this._featureLayer = L.mapbox.featureLayer<LandGeoJSONLayer>();
    }


    setFeatureLayer(geoJson: GeoJSON.FeatureCollection<GeoJSON.GeometryObject>, onLayerAdded: LayerAddedCallback): void {
        if (geoJson === null) { throw new Error("geoJson can not be null."); }

        this.resetFeatureLayer();
        this._featureLayer.on("layeradd", onLayerAdded);
        this._featureLayer.setGeoJSON(geoJson);
    }


    toggleLandMarkers(map: L.Map): void {
        const mapLayer = this._featureLayer;
        if (!mapLayer) { return; }
        const showMarker = mapLayer.getLayers().some(layer => {
            const layerBounds = layer.getBounds();
            const pixelBounds = L.bounds(
                map.latLngToContainerPoint(layerBounds.getNorthEast()),
                map.latLngToContainerPoint(layerBounds.getSouthWest())
            );
            const pixelSize = pixelBounds.getSize();

            return pixelSize.x * pixelSize.y < this._landMarkerThreshold;
        });

        if (!showMarker && this._landMarkerGroup) {
            map.removeLayer(this._landMarkerGroup);
            this._landMarkerGroup = null;
        } else if (showMarker && !this._landMarkerGroup) {
            this._landMarkerGroup = this._createLandMarkerGroup(mapLayer);
            map.addLayer(this._landMarkerGroup);
        }
    }

    toggleLandMarkersOnLayerUpdate(map: L.Map): void {
        // Recreate marker groups if necessary
        if (this._landMarkerGroup) {
            map.removeLayer(this._landMarkerGroup);
            this._landMarkerGroup = null;
        }
        this.toggleLandMarkers(map);
    }


    private _createLandMarkerGroup(layerGroup: L.mapbox.FeatureLayerT<GeoJSONLayer<L.ILayer>>): L.MarkerClusterGroup {
        const markerGroup = L.markerClusterGroup({ animateAddingMarkers: true });

        for (const layer of layerGroup.getLayers()) {
            const marker = L.marker(layer.getBounds().getCenter());

            marker.on("click", function (l: GeoJSONLayer<L.ILayer>): () => void {
                return () => { l.openPopup(); };
            }(layer));

            markerGroup.addLayer(marker);
        }

        return markerGroup;
    }

    private _landMarkerGroup: L.MarkerClusterGroup;
    private _landMarkerThreshold: number;
    private _featureLayer: L.mapbox.FeatureLayerT<LandGeoJSONLayer>;
}



// WEBPACK FOOTER //
// landLayer.ts